import { type SiteId } from '@solinkcorp/api-types';
import {
  canAccessAlarmStatusOnly,
  useImpersonator,
  useUserSession,
} from '@solinkcorp/authentication';
import {
  Badge,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  NavSidebar,
  NavSidebarActionButton,
  NavSidebarActions,
  NavSidebarBadge,
  NavSidebarBody,
  NavSidebarBrand,
  NavSidebarCollapsedOnly,
  NavSidebarFooter,
  NavSidebarGroup,
  NavSidebarHeader,
  NavSidebarItem,
  NavSidebarItemLabel,
  NavSidebarProfile,
  NavSidebarPromo,
  NavSidebarPromoAction,
  NavSidebarPromoDescription,
  NavSidebarPromoDismiss,
  NavSidebarPromoTitle,
  NavSidebarSection,
  NavSidebarToggle,
  NavSidebarToolbar,
} from '@solinkcorp/solink-ui';
import { useQueryParams } from '@solinkcorp/utils/hooks';
import EventFilterActions from 'actions/EventFilterActions';
import { toggleShowKeyboardShortcuts } from 'actions/uiStateActions';
import { NotificationsContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import { type NavTabDescriptor } from 'components/NavigationFrame/DesktopNav/NavTab';
import {
  applyLocationFilter,
  goToCallhomeUser,
  goToHelpCenter,
  goToReferral,
  goToSettings,
  logout,
  switchUsers,
} from 'components/NavigationFrame/navChromeActions';
import { shouldShowNavTab } from 'components/NavigationFrame/shouldShowNavTab';
import { buildUserMenuItems } from 'components/NavigationFrame/UserDropDownMenu/userMenuItems';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import {
  ArrowRightIcon,
  BellIcon,
  CircleHelpIcon,
  KeyboardIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SettingsIcon,
  SunIcon,
  ThumbsUpIcon,
  UserCogIcon,
  UserRoundIcon,
  UsersIcon,
} from 'lucide-react';
import { SELECT_LOCATIONS_CONTEXT } from 'models/AnalyticsConstants';
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import { type FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Link, NavLink, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useControllerState } from 'solox';
import { isKiosk } from 'src/core/utils/kioskUtils';
import { type ColorThemePreference, useColorTheme } from 'src/hooks/useColorTheme';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { AuthStore } from 'stores/AuthStore';
import { LocationStore } from 'stores/LocationStore';
import PreferenceStore from 'stores/PreferenceStore';
import { useDispatch, useSelector } from 'stores/store';
import FeaturesUtil from 'utils/FeaturesUtil';
import { SETTINGS_URL_PREFIX } from 'utils/urls';
import { AGENTS_PAGE, AGENTS_URL, NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT } from 'vd-constants';
import AspenDateFilter, { isDateFilterRoute } from './AspenDateFilter';
import AspenLocationsFilter from './AspenLocationsFilter';
import { useNavGroups } from './navGroups';
import { getNavIcon } from './navIcons';
import { shouldShowAgentsPromo, useAgentsPromoDismissed } from './useAgentsPromoDismissed';

export interface AppSidebarProps {
  /** Nav items the user is permitted to see — the same array that builds the routes. */
  tabs: NavTabDescriptor[];
  inboxCount: number;
  onShowAllNotifications: () => void;
  /**
   * Height of any banner rendered above the app. The sidebar is fixed to the
   * viewport, so without this it would slide underneath the banner.
   */
  topOffsetPx?: number;
  onLiveToggle: (toggle: boolean) => void;
}

/** Shape of NotificationsContext (created in untyped AuthenticatedApp.jsx). */
interface NotificationsContextValue {
  showAllNotifications?: boolean;
}

/**
 * Application sidebar for the redesign shell.
 *
 * Owns everything the design-system component deliberately does not know about:
 * which items this user may see, where they link, badge data, analytics, and
 * the side effects a tab change triggers.
 *
 * Promo / chrome copy stays English until SL-13400 (web locale) and SL-16309
 * (solink-ui label props). Do not invent a parallel i18n path here.
 */
const AppSidebar: FC<AppSidebarProps> = ({
  tabs,
  inboxCount,
  onShowAllNotifications,
  topOffsetPx = 0,
  onLiveToggle,
}) => {
  const locale = useLocale();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useUserSession();
  const isImpersonating = !!useImpersonator();
  const showKeyboardShortcuts = useSelector((state) => state.uiState.showKeyboardShortcuts);
  const { preference: colorThemePreference, setPreference: setColorThemePreference } =
    useColorTheme();
  const { showAllNotifications = false } = useContext(
    NotificationsContext,
  ) as NotificationsContextValue;
  const isSettingsActive = !!useMatch({ path: `${SETTINGS_URL_PREFIX}/*`, end: false });

  const showExperimentalFeatures = PreferenceStore.shouldShowExperimentalFeatures();
  const isTestUser = AuthStore.isTestUser();
  const visibleTabs = useMemo(
    () => tabs.filter((tab) => shouldShowNavTab(tab, showExperimentalFeatures, isTestUser)),
    [tabs, showExperimentalFeatures, isTestUser],
  );
  const groups = useNavGroups(visibleTabs, locale);

  const { locationIds, locationTags } = useSelector((state) => state.filter);
  const locationsById = useFluxStore(LocationStore, (store) => store.getLocationsById());
  const playingCamerasController = usePlayingCamerasContext();
  const numberOfCamerasPlaying = useControllerState(
    playingCamerasController.state,
    (state) => state.playingCameraIds.length,
  );
  const [query] = useQueryParams();
  const cloudStoragePackageId = query.get('storagePackageId');
  const allLocationIds = locationsById ? Object.keys(locationsById) : [];
  const isLocationSelectorDisabled =
    !!numberOfCamerasPlaying || allLocationIds.some((id) => id === cloudStoragePackageId);

  const onLocationsSelected = useCallback(
    ({
      locationIds: nextIds,
      locationTags: nextTags,
    }: {
      locationIds: readonly SiteId[];
      locationTags?: readonly string[];
    }) => {
      applyLocationFilter(dispatch, locationsById, nextIds, nextTags);
    },
    [dispatch, locationsById],
  );

  const handleSelect = useCallback((tab: NavTabDescriptor) => {
    tab.onClick?.();
    AnalyticsLogger.selectNavigationTab({ tab: tab.page });
    EventFilterActions.resetFilters();
  }, []);

  const handleGoToSettings = useCallback(() => goToSettings(navigate), [navigate]);
  const handleGoToHelpCenter = useCallback(() => goToHelpCenter(), []);
  const handleLogout = useCallback(() => logout(navigate), [navigate]);
  const handleSwitchUsers = useCallback(
    () => switchUsers(navigate, locationRef.current),
    [navigate],
  );
  const handleToggleShortcuts = useCallback(() => {
    dispatch(toggleShowKeyboardShortcuts(!showKeyboardShortcuts));
  }, [dispatch, showKeyboardShortcuts]);

  const profileName = user?.profile?.name || user?.profile?.email || '';
  const userEmail = user?.profile?.email || '';
  const userType = user?.profile?.userType;
  const roleLabel = useMemo(() => {
    if (!userType) return undefined;
    const labels = locale.usersPage as Record<string, string | undefined>;
    return labels[userType] ?? userType;
  }, [locale.usersPage, userType]);

  const { dismissed: agentsPromoDismissed, dismiss: dismissAgentsPromo } =
    useAgentsPromoDismissed();
  const isOnAgentsPage = !!useMatch({ path: `${AGENTS_URL}/*`, end: false });
  const showAgentsPromo = shouldShowAgentsPromo({
    hasAgentsTab: visibleTabs.some((tab) => tab.page === AGENTS_PAGE),
    dismissed: agentsPromoDismissed,
    isOnAgentsPage,
  });

  useEffect(() => {
    if (isOnAgentsPage) dismissAgentsPromo();
  }, [isOnAgentsPage, dismissAgentsPromo]);

  const isDemoMode = FeaturesUtil.isDemoMode();
  const isKioskMode = isKiosk();
  const shouldShowLocationSelector = !isDemoMode && !isKioskMode;
  const shouldShowDateFilter = isDateFilterRoute(location.pathname);
  const shouldShowNotifications =
    !isKioskMode &&
    !AuthService.isGuest &&
    !canAccessAlarmStatusOnly(user) &&
    !FeaturesUtil.isLiveViewOnly();

  const menuItems = useMemo(
    () =>
      buildUserMenuItems({
        locale,
        isImpersonating,
        userEmail,
        // Settings / Help already sit in the actions row above the profile.
        omitSettings: true,
        omitHelp: true,
        onGoToSettings: handleGoToSettings,
        onToggleKeyboardShortcuts: handleToggleShortcuts,
        onGoToHelpCenter: handleGoToHelpCenter,
        onGoToReferral: goToReferral,
        onSwitchUsers: handleSwitchUsers,
        onGoToCallhomeUser: goToCallhomeUser,
        onLogout: handleLogout,
      }),
    [
      locale,
      isImpersonating,
      userEmail,
      handleGoToSettings,
      handleToggleShortcuts,
      handleGoToHelpCenter,
      handleSwitchUsers,
      handleLogout,
    ],
  );

  return (
    <NavSidebar
      className={topOffsetPx ? 'inset-y-auto' : undefined}
      style={
        topOffsetPx ? { top: topOffsetPx, height: `calc(100svh - ${topOffsetPx}px)` } : undefined
      }
    >
      <NavSidebarHeader>
        <NavSidebarBrand asChild>
          <Link to="/" data-testid="desk-nav-company-logo" aria-label="Solink home" />
        </NavSidebarBrand>
      </NavSidebarHeader>

      <NavSidebarBody>
        {groups.map((group) => (
          <NavSidebarGroup key={group.id} label={group.label}>
            {group.items.map((tab) => (
              <AppSidebarNavItem key={tab.page} tab={tab} onSelect={handleSelect} />
            ))}
          </NavSidebarGroup>
        ))}
      </NavSidebarBody>

      {(shouldShowDateFilter || shouldShowLocationSelector) && (
        <NavSidebarSection className="gap-2">
          {shouldShowDateFilter && (
            <AspenDateFilter variant="expanded" onLiveToggle={onLiveToggle} />
          )}
          {shouldShowLocationSelector && (
            <AspenLocationsFilter
              variant="expanded"
              selectedLocationIds={locationIds}
              selectedLocationTags={locationTags}
              onLocationsSelected={onLocationsSelected}
              context={SELECT_LOCATIONS_CONTEXT.GLOBAL_SELECTOR}
              disabled={isLocationSelectorDisabled}
            />
          )}
        </NavSidebarSection>
      )}

      {showAgentsPromo && (
        <NavSidebarPromo>
          <NavSidebarPromoTitle>
            Solink Agents
            <Badge variant="primary">Beta</Badge>
            <NavSidebarPromoDismiss
              aria-label="Dismiss Solink Agents promo"
              onClick={dismissAgentsPromo}
            />
          </NavSidebarPromoTitle>
          <NavSidebarPromoDescription>
            Have different agents monitor your video, data and events.
          </NavSidebarPromoDescription>
          <NavSidebarPromoAction asChild>
            <Link to={AGENTS_URL} onClick={dismissAgentsPromo}>
              Set up an agent
              <ArrowRightIcon />
            </Link>
          </NavSidebarPromoAction>
        </NavSidebarPromo>
      )}

      {/*
        Figma collapsed bottom cluster: map-pin / settings / help / bell, then a
        16px gap, then the expand toggle. Expanded keeps the toggle on the same
        row (ml-auto). Single Toggle instance so collapse does not remount it.
      */}
      <NavSidebarToolbar>
        <NavSidebarActions>
          {shouldShowDateFilter && (
            <NavSidebarCollapsedOnly>
              <AspenDateFilter variant="collapsed" onLiveToggle={onLiveToggle} />
            </NavSidebarCollapsedOnly>
          )}
          {shouldShowLocationSelector && (
            <NavSidebarCollapsedOnly>
              <AspenLocationsFilter
                variant="collapsed"
                selectedLocationIds={locationIds}
                selectedLocationTags={locationTags}
                onLocationsSelected={onLocationsSelected}
                context={SELECT_LOCATIONS_CONTEXT.GLOBAL_SELECTOR}
                disabled={isLocationSelectorDisabled}
              />
            </NavSidebarCollapsedOnly>
          )}
          {FeaturesUtil.canViewSettings() && (
            <NavSidebarActionButton
              id="settings-nav-button"
              data-testid="desk-nav-settings"
              aria-label={locale.navigation.settings}
              isActive={isSettingsActive}
              onClick={handleGoToSettings}
            >
              <SettingsIcon />
            </NavSidebarActionButton>
          )}
          {FeaturesUtil.canViewHelpCenter() && (
            <NavSidebarActionButton
              id="help-center"
              data-testid="desk-nav-help"
              aria-label={locale.navigation.helpcenter}
              onClick={handleGoToHelpCenter}
            >
              <CircleHelpIcon />
            </NavSidebarActionButton>
          )}
          {shouldShowNotifications && (
            <NavSidebarActionButton
              aria-label={locale.notifications.notifications}
              isActive={showAllNotifications}
              onClick={onShowAllNotifications}
              data-testid="desk-nav-inbox"
            >
              <BellIcon />
              {inboxCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-interaction-destructive-background-default px-1 text-interaction-destructive-content-default label-sm">
                  {inboxCount > NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT
                    ? `${NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT}+`
                    : inboxCount}
                </span>
              )}
            </NavSidebarActionButton>
          )}
          {/* Chrome copy stays English until SL-13400 — see the component header note. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <NavSidebarActionButton
                aria-label="Color theme"
                tooltip={false}
                data-testid="desk-nav-theme-toggle"
              >
                {colorThemePreference === 'system' ? (
                  <MonitorIcon />
                ) : colorThemePreference === 'light' ? (
                  <SunIcon />
                ) : (
                  <MoonIcon />
                )}
              </NavSidebarActionButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align="start"
              sideOffset={8}
              className="z-[200] min-w-52 max-w-64 select-none border-border-muted bg-background-base p-1 text-foreground-primary shadow-md"
              onCloseAutoFocus={(event) => event.preventDefault()}
            >
              <DropdownMenuLabel className="flex items-center gap-2 px-3 py-2 font-normal text-foreground-secondary label-sm">
                Color theme
              </DropdownMenuLabel>
              {/*
                RadioGroup gives each option role="menuitemradio" + aria-checked, so
                assistive tech announces the active theme. The theme icon is the visual
                selected signal, so the default radio-dot indicator is hidden below.
              */}
              <DropdownMenuRadioGroup
                value={colorThemePreference}
                onValueChange={(value) => setColorThemePreference(value as ColorThemePreference)}
              >
                {(
                  [
                    {
                      value: 'system',
                      label: 'System',
                      Icon: MonitorIcon,
                      testId: 'desk-nav-theme-system',
                    },
                    {
                      value: 'light',
                      label: 'Light',
                      Icon: SunIcon,
                      testId: 'desk-nav-theme-light',
                    },
                    { value: 'dark', label: 'Dark', Icon: MoonIcon, testId: 'desk-nav-theme-dark' },
                  ] as const
                ).map(({ value, label, Icon, testId }) => {
                  const isSelected = colorThemePreference === value;
                  return (
                    <DropdownMenuRadioItem
                      key={value}
                      value={value}
                      data-testid={testId}
                      className={cn(
                        MENU_ITEM_WIDTH_FIX,
                        // Override RadioItem's pl-8 indicator gutter and hide its dot — this
                        // menu uses the accent theme icon as the selected signal instead.
                        // `!hidden` must win over MENU_ITEM_WIDTH_FIX's `[&>span]:!flex`, which
                        // otherwise forces the absolutely-positioned indicator span back visible.
                        'cursor-pointer gap-2 rounded-sm px-3 py-2 !label-md [&>span:first-child]:!hidden',
                        'text-foreground-primary focus:!text-foreground-primary',
                        // Same active treatment as NavSidebarItem: elevated pill + accent icon.
                        isSelected
                          ? 'bg-background-elevated focus:!bg-background-elevated data-[highlighted]:!bg-background-elevated'
                          : 'focus:!bg-background-elevated/60 data-[highlighted]:!bg-background-elevated/60',
                      )}
                    >
                      {/* Color on the icon itself — parent [&_svg] loses to nested wrappers. */}
                      <Icon
                        className={cn(
                          'size-4 shrink-0',
                          isSelected ? 'text-foreground-accent' : 'text-foreground-secondary',
                        )}
                      />
                      <span className="min-w-0 flex-1 truncate">{label}</span>
                    </DropdownMenuRadioItem>
                  );
                })}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </NavSidebarActions>
        <NavSidebarToggle />
      </NavSidebarToolbar>

      <NavSidebarFooter>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <NavSidebarProfile
              initials={getInitials(profileName)}
              name={profileName}
              description={roleLabel}
              data-testid="desk-nav-user-settings"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            sideOffset={8}
            className="z-[200] min-w-52 select-none border-border-muted bg-background-base p-1 text-foreground-primary shadow-md"
          >
            {menuItems.map((item, index) => {
              if (item.isVisible === false) return null;
              // No separators — Aspen profile menu matches flat nav item list.
              if (item.type === 'divider') return null;
              if (item.type === 'label') {
                return (
                  <DropdownMenuLabel
                    key={`label-${index}`}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 font-normal text-foreground-secondary label-sm',
                      '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-foreground-secondary',
                      isImpersonating && 'text-orange-600 [&_svg]:text-orange-600',
                    )}
                  >
                    {getAspenProfileIcon(item)}
                    <span className="truncate">{item.text}</span>
                  </DropdownMenuLabel>
                );
              }
              return (
                <DropdownMenuItem
                  key={item.id ?? `action-${index}`}
                  id={item.id}
                  className={cn(
                    // Match NavSidebarItem: pointer, muted hover, secondary glyphs.
                    'cursor-pointer gap-2 rounded-sm px-3 py-2 text-foreground-primary label-md',
                    'focus:!bg-background-muted focus:!text-foreground-primary',
                    'data-[highlighted]:!bg-background-muted',
                    '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-foreground-secondary',
                    item.className,
                  )}
                  onClick={item.onClick}
                >
                  {getAspenProfileIcon(item)}
                  {item.text}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </NavSidebarFooter>
    </NavSidebar>
  );
};

/** Constrain Solink DropdownMenuItem nested inline-flex wrappers so truncate works. */
const MENU_ITEM_WIDTH_FIX =
  '[&>span]:!flex [&>span]:w-full [&>span]:min-w-0 [&>span>span]:!flex [&>span>span]:w-full [&>span>span]:min-w-0';

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/** Lucide icons so the profile popover matches nav item chrome (not legacy MDI fills). */
function getAspenProfileIcon(item: { id?: string; type: string }) {
  if (item.type === 'label') return <UserRoundIcon />;
  switch (item.id) {
    case 'logout-button':
      return <LogOutIcon />;
    case 'referral':
      return <ThumbsUpIcon />;
    case 'settings-nav-button':
      return <SettingsIcon />;
    case 'help-center':
      return <CircleHelpIcon />;
    case 'switch-users':
      return <UsersIcon />;
    case 'keyboard-shortcuts':
      return <KeyboardIcon />;
    case 'callhome-user':
      return <UserCogIcon />;
    default:
      return <UserRoundIcon />;
  }
}

/** One nav row — `useMatch` keeps active state in sync with nested routes. */
const AppSidebarNavItem: FC<{
  tab: NavTabDescriptor;
  onSelect: (tab: NavTabDescriptor) => void;
}> = ({ tab, onSelect }) => {
  const Icon = getNavIcon(tab.page);
  const href = tab.href || tab.route || '';
  const route = tab.route || tab.href || '';
  const match = useMatch({ path: `${route}/*`, end: false });
  const exact = useMatch({ path: route, end: true });
  const isActive = !!(match || exact);
  const badge =
    tab.badge ??
    (tab.page === AGENTS_PAGE
      ? { label: 'New', tone: 'accent' as const }
      : tab.beta
        ? { label: 'Beta', tone: 'accent' as const }
        : undefined);

  return (
    <NavSidebarItem
      asChild
      isActive={isActive}
      tooltip={tab.label}
      data-testid={`desk-nav-${tab.label}`}
    >
      <NavLink
        to={href}
        onClick={() => onSelect(tab)}
        data-intercom-target={`desk-nav-${tab.label.toLocaleLowerCase()}`}
      >
        <Icon />
        <NavSidebarItemLabel>{tab.label}</NavSidebarItemLabel>
        {badge && <NavSidebarBadge tone={badge.tone}>{badge.label}</NavSidebarBadge>}
      </NavLink>
    </NavSidebarItem>
  );
};

export default AppSidebar;
